import { Bug, Shield, Check, Phone, Star, MapPin } from "lucide-react";
import { Logo, Header } from "../components/Layout";
import { Button } from "../components/ui/button";
import IpswichBreadcrumb from "../components/IpswichBreadcrumb";

const suburbs = ["Ipswich CBD", "Brassall", "Booval", "Bundamba", "Goodna", "Redbank Plains", "Collingwood Park", "Bellbird Park", "Ripley", "Springfield", "North Ipswich", "East Ipswich"];

export default function TermiteInspectionIpswichPage() {
  return (
    <>
      <>
        <title>Termite Inspection Ipswich | Pest Free Ipswich & Brisbane</title>
        <meta name="description" content="Professional termite inspections across Ipswich. Thermal imaging, moisture detection & detailed reports. QBCC licensed. Protect your home — book today." />
      </>

      <Header />
      <div className="min-h-screen bg-slate-50">
        <IpswichBreadcrumb current="Termite Inspection" />
        <section className="bg-gradient-to-br from-[#3E2723] via-[#4E342E] to-[#D2691E]/20 text-white pt-28 pb-16 md:pt-32 md:pb-24">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-[#D2691E]/20 border border-[#D2691E]/30 rounded-full px-4 py-1.5 mb-5">
                  <MapPin className="w-4 h-4 text-[#D2691E]" />
                  <span className="text-sm font-medium text-[#D2691E]">Ipswich & Surrounds</span>
                </div>
                <h1 className="text-4xl md:text-5xl font-black mb-4 leading-tight">
                  Termite Inspection <span className="text-[#D2691E]">Ipswich</span>
                </h1>
                <p className="text-lg text-slate-300 mb-6">
                  Ipswich is one of Queensland's highest termite-risk areas. Don't wait until it's too late — our thorough inspections with thermal imaging catch termites early, saving you thousands in damage.
                </p>
                <div className="flex flex-wrap gap-3 mb-6">
                  <div className="flex items-center gap-1.5 text-sm text-slate-300"><Check className="w-4 h-4 text-[#7ABC32]" />Thermal Imaging</div>
                  <div className="flex items-center gap-1.5 text-sm text-slate-300"><Check className="w-4 h-4 text-[#7ABC32]" />QBCC Licensed</div>
                  <div className="flex items-center gap-1.5 text-sm text-slate-300"><Check className="w-4 h-4 text-[#7ABC32]" />Detailed Report</div>
                </div>
                <div className="flex items-center gap-3">
                  <a href="tel:0737081422" className="flex items-center gap-2 bg-[#7ABC32] hover:bg-[#6AAD28] text-white font-semibold px-6 py-3 rounded-full transition-all">
                    <Phone className="w-5 h-5" /> (07) 3708 1422
                  </a>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />)}
                    <span className="text-sm text-slate-300 ml-1">4.9 on Google</span>
                  </div>
                </div>
              </div>
              <div className="flex justify-center">
                <Logo className="h-32 md:h-40 opacity-90" />
              </div>
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="py-12 md:py-16">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-800">What's Included in Our Inspection</h2>
              <p className="text-slate-600 mt-2">From $150 — comprehensive termite inspection with full report</p>
            </div>
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="bg-gradient-to-r from-[#D2691E] to-[#8B4513] text-white p-4 flex items-center gap-3">
                <Bug className="w-6 h-6" />
                <h3 className="text-xl font-bold">Termite Inspection</h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    "Full property termite inspection",
                    "Thermal imaging camera survey",
                    "Moisture detection & readings",
                    "Subfloor inspection (where accessible)",
                    "Roof void inspection",
                    "External perimeter & garden beds",
                    "Fence lines & retaining walls",
                    "Detailed report with photos",
                    "Risk assessment & recommendations",
                    "Treatment plan if termites found"
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-[#D2691E] flex-shrink-0" />
                      <span className="text-sm text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t pt-4 mt-4 flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div>
                    <span className="text-3xl font-bold text-[#D2691E]">From $150</span>
                    <span className="text-sm text-slate-500 ml-2">(price varies by property size)</span>
                  </div>
                  <a href="tel:0737081422" className="flex items-center gap-2 bg-[#7ABC32] hover:bg-[#6AAD28] text-white font-semibold px-6 py-3 rounded-full transition-all">
                    <Phone className="w-5 h-5" /> Book Now: (07) 3708 1422
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Ipswich is High Risk */}
        <section className="py-12 bg-white">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-slate-800 text-center mb-8">Why Ipswich is High Risk for Termites</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { title: "Clay Soil", desc: "Ipswich's heavy clay soil retains moisture, creating ideal conditions for subterranean termites to thrive and travel.", color: "#D2691E" },
                { title: "Older Timber Homes", desc: "Many Ipswich homes are timber-framed Queenslanders — termites' favourite food source. Regular inspections are critical.", color: "#D2691E" },
                { title: "Bushland Proximity", desc: "Suburbs backing onto bushland and creek corridors have constant termite pressure from surrounding colony networks.", color: "#D2691E" },
                { title: "New Developments", desc: "Even new builds in Ripley, Springfield and Redbank Plains are at risk — disturbed soil during construction attracts termites.", color: "#D2691E" },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 p-4 rounded-lg bg-slate-50">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: item.color + '15' }}>
                    <Bug className="w-5 h-5" style={{ color: item.color }} />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800">{item.title}</h3>
                    <p className="text-sm text-slate-600 mt-1">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Suburbs */}
        <section className="py-12 bg-slate-50">
          <div className="max-w-5xl mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold text-slate-800 mb-6">Serving All Ipswich Suburbs</h2>
            <div className="flex flex-wrap justify-center gap-2">
              {suburbs.map(s => (
                <span key={s} className="bg-white border border-slate-200 rounded-full px-4 py-2 text-sm font-medium text-slate-700">{s}</span>
              ))}
            </div>
            <p className="text-slate-500 text-sm mt-4">And all surrounding areas</p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-12 bg-gradient-to-r from-[#D2691E] to-[#8B4513] text-white text-center">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Don't Wait Until It's Too Late</h2>
            <p className="text-lg text-white/80 mb-6">Annual termite inspections can save you tens of thousands in damage. Call us today.</p>
            <a href="tel:0737081422" className="inline-flex items-center gap-2 bg-white text-[#D2691E] font-bold px-8 py-3 rounded-full hover:bg-slate-100 transition-all">
              <Phone className="w-5 h-5" /> Call (07) 3708 1422
            </a>
          </div>
        </section>
      </div>
    </>
  );
}
